"use strict";

/**
 * SageExportController
 *
 * Unified generation screen for both SAGE Invoice Export and SAGE Receipt
 * Export. Tabs at the top switch between the two report types — only the
 * tabs the user has permission for are shown. The project + From/To date
 * inputs and Generate flow are otherwise identical.
 *
 * Replaces the two earlier separate screens (SageInvoiceExportController,
 * SageReceiptExportController) which were merged into a single page per
 * WEB-3413 iter-1 navigation restructure.
 *
 * Permissions (post-WEB-3328 iter-16 rename — all DMS_SAGE_EXPORT_* prefix):
 *   - `DMS_SAGE_EXPORT_INVOICES_GENERATE` (global) — enables the Invoice tab
 *   - `DMS_SAGE_EXPORT_INSTRUMENTS_GENERATE` (global) — enables the Receipt tab
 *   - `DMS_SAGE_EXPORT_HISTORY_VIEW` (global) — gates the History button visibility
 *   - `DMS_VW_PROJ` (property-level) — implicit via the property-list filter
 *   Page itself opens if the user has either generate permission; the tab
 *   logic falls back to the one they have if they only hold one.
 *
 * Project picker:
 *   - LISTED-status only (filtered both server-side via `status` query
 *     param and defensively client-side).
 *   - Persisted under `lastPropertyId` so the same selection follows the
 *     user across Sales & Payments + the SAGE pages.
 *   - Rendered as a searchable niceSelect.
 */
class SageExportController {

    constructor($scope, SSHttpService, SSAlertService, SSUserService, SSUtilService, SageReportsService, $timeout) {
        const self = this;

        self.$scope = $scope;
        self.SSHttpService = SSHttpService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.SSUtilService = SSUtilService;
        self.SageReportsService = SageReportsService;
        self.$timeout = $timeout;

        self.TAB_INVOICE = 'invoice';
        self.TAB_RECEIPT = 'receipt';

        // Receipt-type split (WEB-3536). The receipts/generate endpoint now
        // requires a `receipt_type` discriminator:
        //   - 'principal' → exports payment instruments where is_gst_payment
        //     is null/0 (principal payments)
        //   - 'gst'       → exports payment instruments where is_gst_payment = 1
        // The two are tracked server-side under separate report types
        // (sage_receipt_principal / sage_receipt_gst). Only relevant on the
        // Receipt tab — the Invoice endpoint neither expects nor accepts it.
        self.RECEIPT_TYPE_PRINCIPAL = 'principal';
        self.RECEIPT_TYPE_GST = 'gst';

        // Permissions (post-WEB-3328 iter-16 rename — all DMS_SAGE_EXPORT_* prefix).
        // Each gate is the global flag; per-property scope comes from DMS_VW_PROJ,
        // which the property-list endpoint already filters by.
        self.PERMISSION_INVOICE = 'DMS_SAGE_EXPORT_INVOICES_GENERATE';
        self.PERMISSION_RECEIPT = 'DMS_SAGE_EXPORT_INSTRUMENTS_GENERATE';
        self.PERMISSION_HISTORY_VIEW = 'DMS_SAGE_EXPORT_HISTORY_VIEW';
        // Code Mappings page now has its own VIEW permission (Waseem iter-26
        // — backend split the single _HISTORY_REGENERATE gate into four
        // dedicated per-action permissions). The "Payment Code Mappings"
        // button on this Export page only needs the VIEW perm — Add/Edit/
        // Delete gates live on the Code Mappings page itself.
        self.PERMISSION_CODE_MAPPINGS = 'DMS_SAGE_EXPORT_CODE_MAPPING_VIEW';

        self.canAccessInvoice = self.SSUserService.hasPermission(self.PERMISSION_INVOICE);
        self.canAccessReceipt = self.SSUserService.hasPermission(self.PERMISSION_RECEIPT);
        self.canAccessHistory = self.SSUserService.hasPermission(self.PERMISSION_HISTORY_VIEW);
        self.canAccessCodeMappings = self.SSUserService.hasPermission(self.PERMISSION_CODE_MAPPINGS);
        self.has_access = self.canAccessInvoice || self.canAccessReceipt;

        // Default to whichever tab the user can actually use.
        self.activeTab = self.canAccessInvoice ? self.TAB_INVOICE : self.TAB_RECEIPT;

        self.properties = null;
        self.selectedPropertyId = null;
        // Receipt-type selector (Receipt tab only) — default to principal.
        self.receiptType = self.RECEIPT_TYPE_PRINCIPAL;
        self.dateFrom = null;
        self.dateTo = null;
        self.displayDateFrom = false;
        self.displayDateTo = false;
        self.datepickerOptions = { showWeeks: false };
        self.existingReport = null;
        self.submitting = false;

        if (self.has_access) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.prepareDeveloperProperties();
        self.initDatepickers();
    }

    /**
     * Wire the From / To Date inputs to jQuery UI datepicker — same widget
     * used by the Sales & Payments / SPA Dates pages for visual consistency.
     * `onSelect` pushes the picked value back into ng-model via $scope.$apply
     * because jQuery UI fires its select callback outside Angular's digest.
     * Wrapped in $timeout(0) so the DOM nodes are in place before init.
     */
    initDatepickers() {
        const self = this;
        self.$timeout(function () {
            $('#sageExportFromDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () { self.dateFrom = dateText; });
                }
            });
            $('#sageExportToDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () { self.dateTo = dateText; });
                }
            });
        });
    }

    prepareDeveloperProperties() {
        const self = this;

        let params = { status: SHOWSUITE.PROPERTY_STATUS_LISTED };

        loading(true);
        self.SSHttpService.getAPIRequest('sec/developer/properties/list', params).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            let all = (response.data && response.data.properties) || [];
            // Defensive client-side filter: the backend may not honour the
            // status query param on sec/developer/properties/list.
            self.properties = all.filter(function (p) {
                return p && p.status === SHOWSUITE.PROPERTY_STATUS_LISTED;
            });

            // Cross-page memory — share `lastPropertyId` with Sales & Payments
            // and the SAGE Report History page so the same project stays
            // selected across navigation.
            let saved = self.SSUtilService.getFromLocalStorage('lastPropertyId');
            if (saved && self.properties.some(p => String(p.id) === String(saved))) {
                self.selectedPropertyId = parseInt(saved, 10);
            }

            self.$timeout(function () {
                if ($('select.dashboard-select#property-select').niceSelect) {
                    $('select.dashboard-select#property-select').niceSelect('update');
                }
            });
        });
    }

    onTabChange(tab) {
        const self = this;
        if (self.activeTab === tab) { return; }
        // Block switching to a tab the user can't access (also gated in the
        // view, but defensive).
        if (tab === self.TAB_INVOICE && !self.canAccessInvoice) { return; }
        if (tab === self.TAB_RECEIPT && !self.canAccessReceipt) { return; }
        self.activeTab = tab;
        self.existingReport = null; // Stale 409 warning shouldn't carry across tabs.
    }

    onPropertyChange() {
        const self = this;
        self.existingReport = null;
        if (self.selectedPropertyId) {
            self.SSUtilService.saveToLocalStorage('lastPropertyId', self.selectedPropertyId);
        }
    }

    /**
     * Switching receipt type (principal ⇄ gst) clears any stale 409 warning.
     * Each receipt type is tracked under its own report type server-side, so
     * an "already generated" notice raised for principal must not carry over
     * into the gst view (and vice-versa).
     */
    onReceiptTypeChange() {
        this.existingReport = null;
    }

    openDateFrom() { this.displayDateFrom = true; }
    openDateTo() { this.displayDateTo = true; }

    /**
     * Generate button is enabled when project + both dates are picked, no
     * submit in flight, and the user actually has the active tab's perm.
     */
    canGenerate() {
        const self = this;
        let perm = self.activeTab === self.TAB_INVOICE ? self.PERMISSION_INVOICE : self.PERMISSION_RECEIPT;
        // Receipt exports must carry a receipt_type — guard against it being
        // unset (defensive; it defaults to principal).
        if (self.activeTab === self.TAB_RECEIPT && !self.receiptType) { return false; }
        return !!(self.selectedPropertyId && self.dateFrom && self.dateTo && !self.submitting
            && self.SSUserService.hasPermission(perm));
    }

    /**
     * Submit the generation request for the active tab. The service hands
     * us back `{ blob, filename }` on success (the backend streams CSV
     * with Content-Disposition: attachment) or an Error-with-code on
     * failure (e.g. 409 with the existing version's metadata).
     */
    generate() {
        const self = this;

        if (!self.canGenerate()) { return; }

        if (self.dateFrom > self.dateTo) {
            self.SSAlertService.danger('Validation Error', 'From Date must be on or before To Date.');
            return;
        }

        let payload = {
            property_id: self.selectedPropertyId,
            date_from: self._fmtDate(self.dateFrom),
            date_to: self._fmtDate(self.dateTo)
        };

        // Receipt exports require a receipt_type discriminator (WEB-3536);
        // the invoice endpoint neither expects nor accepts it.
        if (self.activeTab === self.TAB_RECEIPT) {
            payload.receipt_type = self.receiptType;
        }

        self.submitting = true;
        self.existingReport = null;
        loading(true);

        let request = self.activeTab === self.TAB_INVOICE
            ? self.SageReportsService.generateInvoices(payload)
            : self.SageReportsService.generateReceipts(payload);

        let kindLabel = self.activeTab === self.TAB_INVOICE ? 'Invoice' : 'Receipt';

        request.then(function (result) {
            loading(false);
            self.submitting = false;

            if (result instanceof Error) {
                if (result.code === 409 && result.data) {
                    self.existingReport = result.data;
                    self.SSAlertService.warning(
                        'Report already generated',
                        'A report already exists for this project and date range. ' +
                        'To re-emit, an admin must unlock at least one record in the range ' +
                        'and then click Regenerate on the matching row in SAGE Export History.'
                    );
                    return;
                }
                self.SSAlertService.parseAndDisplayError(result);
                return;
            }

            self.SageReportsService.triggerCsvDownload(result.blob, result.filename);
            self.SSAlertService.success(
                kindLabel + ' export generated',
                'Your browser has been prompted to save ' + (result.filename || 'the CSV file') + '.'
            );
        });
    }

    openExistingDownload() {
        const self = this;
        let r = self.existingReport || {};
        let url = r.url || r.s3_file_url || r.presigned_url;
        if (url) {
            window.open(url, '_blank');
        }
    }

    /**
     * Backend wants `yyyy-mm-dd`. jQuery UI datepicker pushes a `dd/mm/yyyy`
     * string into ng-model on select; the legacy uib-datepicker pushed a
     * Date object. Handle all three.
     */
    _fmtDate(d) {
        if (!d) { return null; }
        if (typeof d === 'string') {
            let parts = d.split('/');
            if (parts.length === 3 && parts[2].length === 4) {
                return parts[2] + '-' + parts[1] + '-' + parts[0];
            }
            return d;
        }
        let pad = (n) => (n < 10 ? '0' + n : '' + n);
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    }
}

SageExportController.$inject = [
    '$scope',
    'SSHttpService',
    'SSAlertService',
    'SSUserService',
    'SSUtilService',
    'SageReportsService',
    '$timeout'
];
app.controller('SageExportController', SageExportController);
